import { useEffect } from "react";

interface UseClickOutsideOptions {
  excludeSelector?: string;
}

/**
 * Hook that handles clicks outside of a referenced element
 * @param ref - React ref object for the element
 * @param callback - Function to call when click outside is detected
 * @param options - Optional configuration (excludeSelector)
 */
export const useClickOutside = (
  ref: React.RefObject<HTMLDivElement | null>,
  callback: () => void,
  options?: UseClickOutsideOptions
) => {
  const { excludeSelector } = options || {};

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (ref.current?.contains(target)) return;

      if (excludeSelector) {
        const htmlTarget = target as HTMLElement;
        if (htmlTarget.closest(excludeSelector)) return;
      }

      callback();
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, excludeSelector]);
};
